import { supabase } from '../supabaseClient';

export const carrierColumnMappingsService = {
    // Obtener todos los mapeos de una agencia
    async getByAgency(agencyId) {
        const { data, error } = await supabase
            .from('carrier_column_mappings')
            .select('*')
            .eq('agency_id', agencyId)
            .order('insurance_type', { ascending: true })
            .order('carrier_name', { ascending: true });
        if (error) throw error;
        return data || [];
    },

    // Obtener mapeos por tipo de seguro
    async getByAgencyAndType(agencyId, insuranceType) {
        const { data, error } = await supabase
            .from('carrier_column_mappings')
            .select('*')
            .eq('agency_id', agencyId)
            .eq('insurance_type', insuranceType)
            .order('carrier_name', { ascending: true });
        if (error) throw error;
        return data || [];
    },

    // Obtener mapeo específico por tipo de seguro, carrier y agencia
    async getByAgencyTypeAndCarrier(agencyId, insuranceType, carrierName) {
        const { data, error } = await supabase
            .from('carrier_column_mappings')
            .select('*')
            .eq('agency_id', agencyId)
            .eq('insurance_type', insuranceType)
            .eq('carrier_name', carrierName)
            .maybeSingle();
        if (error) throw error;
        return data;
    },

    // Obtener mapeo específico por carrier y agencia (retrocompatibilidad)
    async getByAgencyAndCarrier(agencyId, carrierName) {
        const { data, error } = await supabase
            .from('carrier_column_mappings')
            .select('*')
            .eq('agency_id', agencyId)
            .eq('carrier_name', carrierName)
            .maybeSingle();
        if (error) throw error;
        return data;
    },

    // Obtener mapeo para un agente (de su agencia asociada) - filtrado por tipo de seguro opcional
    async getForAgent(agentId, insuranceType = null) {
        // Primero obtener la agencia del agente
        const { data: agentProfile, error: profileError } = await supabase
            .from('profiles')
            .select('agency_id')
            .eq('id', agentId)
            .eq('role', 'agent')
            .single();

        if (profileError || !agentProfile?.agency_id) {
            throw new Error('Agente no tiene agencia asociada');
        }

        // Obtener mapeos de la agencia, filtrados por tipo si se especifica
        if (insuranceType) {
            return this.getByAgencyAndType(agentProfile.agency_id, insuranceType);
        }
        return this.getByAgency(agentProfile.agency_id);
    },

    // Crear o actualizar mapeo
    // NOTA: La restricción UNIQUE ahora es (agency_id, mapping_name)
    async upsert(mapping) {
        // Si tiene ID, actualizar directamente
        if (mapping.id) {
            const { id, ...updates } = mapping;
            return this.update(id, updates);
        }

        if (!mapping.agency_id || !mapping.mapping_name) {
            throw new Error('agency_id y mapping_name son requeridos para crear/actualizar un mapeo');
        }

        // Verificar si ya existe un mapeo con este nombre para esta agencia
        const { data: existing, error: searchError } = await supabase
            .from('carrier_column_mappings')
            .select('id')
            .eq('agency_id', mapping.agency_id)
            .eq('mapping_name', mapping.mapping_name)
            .maybeSingle();

        if (searchError) throw searchError;

        if (existing) {
            // Si existe, actualizar
            return this.update(existing.id, mapping);
        } else {
            // Si no existe, crear
            const { id, ...payload } = mapping; // Asegurar que id no va en el insert
            const { data, error } = await supabase
                .from('carrier_column_mappings')
                .insert([payload])
                .select()
                .single();

            if (error) throw error;
            return data;
        }
    },

    // Actualizar mapeo existente
    async update(id, updates) {
        const { data, error } = await supabase
            .from('carrier_column_mappings')
            .update(updates)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    // Eliminar mapeo
    async delete(id) {
        const { error } = await supabase
            .from('carrier_column_mappings')
            .delete()
            .eq('id', id);
        if (error) throw error;
    },

    // Eliminar mapeo por carrier y agencia
    async deleteByCarrier(agencyId, carrierName) {
        const { error } = await supabase
            .from('carrier_column_mappings')
            .delete()
            .eq('agency_id', agencyId)
            .eq('carrier_name', carrierName);
        if (error) throw error;
    }
};
